import type { HubResponseProps } from '@readme/iso';

import loadable from '@loadable/component';
import React from 'react';

import useIsDryRunMode from '@core/hooks/useIsDryRunMode';
import useIsMigrationPreview from '@core/hooks/useIsMigrationPreview';
import { InitializeSuperHubStore, useSuperHubStore } from '@core/store';

import ExposeLocationProxy from './components/ExposeLocationProxy';

/**
 * Code-splitting our view containers to avoid loading unnecessary JS when
 * loading the non-admin version of the SuperHub.
 */
const AdminEditorContainer = loadable(() => import('./Containers/AdminEditorContainer'), { ssr: true });
const ViewContainer = loadable(() => import('./Containers/ViewContainer'), { ssr: true });
const FramePreviewContainer = loadable(() => import('./Containers/FramePreviewContainer'), { ssr: true });

export interface SuperHubProps extends HubResponseProps {
  children?: React.ReactNode;
}

/**
 * Render this only when `isFramePreview` prop is false, which indicates we are
 * rendering the Hub inline in the main app and not inside an iframe.
 */
function InlineContent(props: SuperHubProps) {
  const [isSuperHub, userRole, isGod] = useSuperHubStore(s => [s.isSuperHub, s.user.role, s.user.isGod]);

  // Is this a project that is currently being migrated with the migration preview enabled?
  // When the app runs in a migration preview, we only render the view container even for project admins.
  const { isMigrationPreview } = useIsMigrationPreview();
  const { isDryRunMode } = useIsDryRunMode();

  // Only allow edit mode if not in migration preview mode or dry run mode, unless user is god
  const isEditableInMigrationPreview = (!isMigrationPreview && !isDryRunMode) || isGod;

  if (props.isFramePreview) {
    throw new Error('InlineContent should only be used when isFramePreview is false');
  }

  // Only admins and team editors can access the full admin UI container.
  if ((userRole === 'admin' || userRole === 'editor') && isEditableInMigrationPreview) {
    return <AdminEditorContainer {...props} />;
  }

  // Team viewers can access a limited admin container with view-only access.
  if (userRole === 'viewer') {
    /**
     * @todo Replace this with a new TeamViewerContainer when implemented
     * @link https://linear.app/readme-io/issue/RM-14455/create-new-readonly-ui-for-the-teammate-viewer-to-only-view-and-review
     */
    return <ViewContainer {...props} />;
  }

  // All other SuperHub users can only see readonly
  if (isSuperHub) {
    return <ViewContainer {...props} />;
  }

  // For non-superhub projects, render content as is.
  return <>{props.children}</>;
}

/**
 * Render this only when `isFramePreview` prop is true, which indicates we are
 * rendering the Hub inside an iframe.
 *
 * ⚠️ This is loaded from an iframe mounted from our edit container.
 */
function FrameContent(props: SuperHubProps) {
  const [isSuperHub, userRole] = useSuperHubStore(s => [s.isSuperHub, s.user.role]);

  if (!props.isFramePreview) {
    throw new Error('FrameContent should only be used when isFramePreview is true');
  }

  // For non-superhub projects, render content as is.
  if (!isSuperHub) {
    return <>{props.children}</>;
  }

  // Only admins and editors get the full frame preview experience.
  if (userRole !== 'admin' && userRole !== 'editor') {
    return <ViewContainer {...props} />;
  }

  return <FramePreviewContainer {...props} />;
}

/**
 * Top-level wrapper provides our required context that all sub-components need.
 * This allows our top-level SuperHub component to also access context values.
 */
export default function SuperHub({ children, ...props }: SuperHubProps) {
  return (
    <InitializeSuperHubStore
      currentBranch={props.branch}
      currentVersion={props.gitVersion}
      language={props.i18n.language}
    >
      <ExposeLocationProxy />

      {props.isFramePreview ? (
        <FrameContent {...props}>{children}</FrameContent>
      ) : (
        <InlineContent {...props}>{children}</InlineContent>
      )}
    </InitializeSuperHubStore>
  );
}
